import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Gauge, RotateCcw } from "lucide-react";
import { TABS, getScenario } from "./scenarios";
import DesktopStage from "./DesktopStage";
import RunConsole from "./RunConsole";
import { cn } from "@/lib/cn";

// Orchestrates the whole walkthrough: one timeline drives both halves — the
// mock PC on the left and the dashboard console on the right — so the story
// reads as a single round trip instead of two animations that happen to agree.

const IDLE_STAGE = {
  browserOpen: false,
  url: "about:blank",
  cursor: { x: 40, y: 200, visible: false },
  active: null,
  values: { card: "", exp: "", cvc: "" },
  paying: false,
  hang: false,
  receipt: false,
  shutter: 0,
  ripple: 0,
  toast: null,
  tray: "idle",
};

const TARGET_URL = "app.acme.com/checkout";
const SPEEDS = [1, 2];

export default function ExeDemo() {
  const reduced = useReducedMotion();

  const [tab, setTab] = useState("ai");
  const [speed, setSpeed] = useState(1);
  const [phase, setPhase] = useState("idle");
  const [stage, setStage] = useState(IDLE_STAGE);
  const [steps, setSteps] = useState([]);
  const [shots, setShots] = useState([]);
  const [suiteState, setSuiteState] = useState([]);
  const [result, setResult] = useState(null);

  const scenario = getScenario(tab);

  // Timers are the whole engine — every beat is a setTimeout, cancelled as one
  // on restart, tab change and unmount so a stale run can never write state.
  const timers = useRef([]);
  const speedRef = useRef(speed);
  speedRef.current = speed;

  const clearTimers = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  }, []);

  const reset = useCallback(() => {
    clearTimers();
    setPhase("idle");
    setStage(IDLE_STAGE);
    setSteps([]);
    setShots([]);
    setSuiteState([]);
    setResult(null);
  }, [clearTimers]);

  const play = useCallback(
    (id) => {
      const sc = getScenario(id);
      clearTimers();
      setPhase("dispatch");
      setStage(IDLE_STAGE);
      setSteps([]);
      setShots([]);
      setSuiteState([]);
      setResult(null);

      const at = (ms, fn) => timers.current.push(setTimeout(fn, ms / speedRef.current));
      const patch = (ms, next) =>
        at(ms, () => setStage((prev) => ({ ...prev, ...next })));
      const moveTo = (ms, point) => {
        if (!point) return;
        patch(ms, { cursor: { x: point.x, y: point.y, visible: true } });
      };
      const typeInto = (ms, field, text, dur) => {
        patch(ms, { active: field });
        const tick = Math.max(20, dur / text.length);
        for (let i = 1; i <= text.length; i += 1) {
          at(ms + i * tick, () =>
            setStage((prev) => ({
              ...prev,
              values: { ...prev.values, [field]: text.slice(0, i) },
            }))
          );
        }
        at(ms + dur + 120, () => setStage((prev) => ({ ...prev, active: null })));
      };
      const markSuite = (ms, index, value) => {
        if (index == null) return;
        at(ms, () =>
          setSuiteState((prev) => {
            const next = [...prev];
            next[index] = value;
            return next;
          })
        );
      };

      const gap = sc.gap;

      // 1 — the dashboard pushes the command down the socket, the tray wakes up.
      at(700, () => {
        setPhase("running");
        setStage((prev) => ({
          ...prev,
          tray: "running",
          toast: { title: sc.trayTitle, body: sc.trayBody },
        }));
      });
      patch(3000, { toast: null });

      // 2 — the agent works, step by step, in its own browser.
      const base = 1250;
      sc.steps.forEach((step, i) => {
        const start = base + i * gap;

        at(start, () =>
          setSteps((prev) => [...prev, { ...step, status: "run", ai: sc.ai || step.ai }])
        );
        markSuite(start, step.suite, "run");

        switch (step.act) {
          case "boot":
            patch(start, { browserOpen: true });
            patch(start + 300, { cursor: { x: 70, y: 70, visible: true } });
            break;
          case "nav": {
            const chars = TARGET_URL.length;
            for (let c = 1; c <= chars; c += 1) {
              at(start + 120 + c * 14, () =>
                setStage((prev) => ({ ...prev, url: TARGET_URL.slice(0, c) }))
              );
            }
            break;
          }
          case "type":
            moveTo(start, step.point);
            typeInto(start + 260, step.field, step.value, gap * 0.45);
            break;
          case "type2":
            moveTo(start, step.point);
            typeInto(start + 200, "exp", "12/29", gap * 0.2);
            typeInto(start + gap * 0.4, "cvc", "311", gap * 0.18);
            break;
          case "click":
          case "heal":
            moveTo(start, step.point);
            at(start + gap * 0.42, () =>
              setStage((prev) => ({ ...prev, ripple: prev.ripple + 1, paying: true }))
            );
            break;
          case "wait":
            patch(start, { paying: true });
            break;
          case "fail":
            patch(start, { paying: true });
            patch(start + gap * 0.45, { hang: true });
            break;
          case "pass":
            // Only the step that actually asserts a receipt shows one — the
            // suite's login/search assertions must not paint a paid receipt.
            patch(start + gap * 0.3, { paying: false, receipt: !!step.receipt });
            break;
          default:
            break;
        }

        // resolve the step just before the next one starts
        const settle = start + gap * 0.72;
        at(settle, () => {
          setSteps((prev) =>
            prev.map((s, idx) =>
              idx === i ? { ...s, status: step.fail ? "bad" : "ok" } : s
            )
          );
          if (step.shot) {
            setShots((prev) => [
              ...prev,
              { id: `${sc.id}-${i}`, label: step.shot, failed: !!step.fail },
            ]);
            setStage((prev) => ({ ...prev, shutter: prev.shutter + 1 }));
          }
        });
        markSuite(settle, step.suiteEnd ? step.suite : null, step.suiteEnd);
      });

      // 3 — verdict, cost, and the bug (or the receipt).
      at(base + sc.steps.length * gap + 250, () => {
        setPhase("idle");
        setResult(sc.result);
        setStage((prev) => ({
          ...prev,
          tray: sc.result.verdict === "PASSED" ? "ok" : "bad",
          cursor: { ...prev.cursor, visible: false },
        }));
      });
    },
    [clearTimers]
  );

  // Start once the demo is actually on screen — never behind the fold, and
  // never while the visitor is reading something else.
  const hostRef = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const node = hostRef.current;
    if (!node || reduced) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started.current) {
            started.current = true;
            play("ai");
          }
        });
      },
      { threshold: 0.3 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [play, reduced]);

  useEffect(() => clearTimers, [clearTimers]);

  const selectTab = (id) => {
    setTab(id);
    reset();
    started.current = true;
    timers.current.push(setTimeout(() => play(id), 220));
  };

  const activeTab = TABS.find((t) => t.id === tab);

  return (
    <div ref={hostRef}>
      {/* scenario picker — three stories, one stage */}
      <div className="flex flex-col items-center gap-3">
        <div className="flex w-full max-w-xl gap-1.5 rounded-2xl border border-slate-200 bg-white p-1.5 shadow-soft dark:border-white/10 dark:bg-ink-900">
          {TABS.map((t) => {
            const Icon = t.icon;
            const selected = t.id === tab;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => selectTab(t.id)}
                aria-pressed={selected}
                className={cn(
                  "flex flex-1 items-center justify-center gap-1.5 rounded-xl px-2 py-2 text-xs font-semibold transition-all sm:text-sm",
                  selected
                    ? "bg-brand-600 text-white shadow-soft"
                    : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/5"
                )}
              >
                <Icon size={14} className="shrink-0" />
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center justify-center gap-2">
          <p className="text-xs text-slate-500 dark:text-slate-400">{activeTab.hint}</p>
          <span className="hidden text-slate-300 dark:text-slate-600 sm:inline">·</span>
          <button
            type="button"
            onClick={() => play(tab)}
            className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-semibold text-brand-600 transition-colors hover:bg-brand-50 dark:text-brand-300 dark:hover:bg-white/5"
          >
            <RotateCcw size={13} /> Replay demo
          </button>
          <button
            type="button"
            onClick={() => setSpeed((s) => SPEEDS[(SPEEDS.indexOf(s) + 1) % SPEEDS.length])}
            className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-semibold text-slate-500 transition-colors hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-white/5"
          >
            <Gauge size={13} /> {speed}× speed
          </button>
        </div>
      </div>

      {/* the stage: PC ←websocket→ dashboard */}
      <div className="mt-6 grid gap-4 lg:grid-cols-[minmax(0,1.5fr)_2.5rem_minmax(0,22rem)] lg:gap-0">
        <DesktopStage stage={stage} reduced={reduced} />

        {/* the link between them — the whole point of the product */}
        <div className="relative hidden lg:block">
          <div className="absolute inset-y-10 left-1/2 w-px -translate-x-1/2 bg-[repeating-linear-gradient(to_bottom,rgba(148,163,184,0.6)_0_5px,transparent_5px_11px)]" />
          {/* the command travels down to the PC, results stream back up */}
          {phase === "dispatch" && !reduced && (
            <motion.span
              initial={{ top: "10%", opacity: 0 }}
              animate={{ top: "88%", opacity: [0, 1, 1, 0] }}
              transition={{ duration: 0.8, ease: "easeInOut" }}
              className="absolute left-1/2 h-2 w-2 -translate-x-1/2 rounded-full bg-brand-500 shadow-glow"
            />
          )}
          {phase === "running" && !reduced && (
            <motion.span
              initial={{ top: "88%", opacity: 0 }}
              animate={{ top: "10%", opacity: [0, 1, 1, 0] }}
              transition={{ duration: 1.2, repeat: Infinity, ease: "linear" }}
              className="absolute left-1/2 h-2 w-2 -translate-x-1/2 rounded-full bg-brand-500 shadow-glow"
            />
          )}
          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rotate-90 whitespace-nowrap bg-slate-50 px-1.5 text-[0.5625rem] font-bold uppercase tracking-widest text-slate-400 dark:bg-ink-950">
            websocket
          </span>
        </div>

        <RunConsole
          scenario={scenario}
          phase={phase}
          steps={steps}
          shots={shots}
          suiteState={suiteState}
          result={result}
          onRun={() => play(tab)}
        />
      </div>

      {/* the punchline: the second run of the same test is free */}
      <AnimatePresence>
        {result?.compare && (
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="mt-4 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-soft dark:border-white/10 dark:bg-ink-900"
          >
            <p className="text-sm font-semibold text-slate-800 dark:text-white">
              Same test, second time — the AI is no longer in the loop.
            </p>
            <div className="flex flex-wrap gap-6">
              <div>
                <span className="block text-[0.625rem] font-bold uppercase tracking-wider text-slate-400">
                  First AI run
                </span>
                <span className="font-mono text-sm text-slate-700 dark:text-slate-200">
                  27s · $0.042 · 1,240 tok
                </span>
              </div>
              <div>
                <span className="block text-[0.625rem] font-bold uppercase tracking-wider text-slate-400">
                  Every replay after
                </span>
                <span className="font-mono text-sm font-bold text-emerald-600 dark:text-emerald-400">
                  6s · $0.000 · 0 tok
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
